import React from 'react';
import { usePaymentsPaused } from '../../hooks/usePaymentsPaused';

const COMPANY_PHONE = process.env.REACT_APP_COMPANY_PHONE || '+91 98765 43210';

// Fallback for orgs who'd rather renew manually while online checkout is being
// tested in Razorpay Test Mode, or just prefer the human touch — kept
// alongside the real checkout button, not replaced by it.
const buildWhatsAppLink = (plan) => {
  const msg = encodeURIComponent(`Hi! I would like to renew my CRM subscription with the "${plan.displayName || plan.name}" plan. Please help.`);
  return `https://wa.me/${COMPANY_PHONE.replace(/\D/g, '')}?text=${msg}`;
};

export const FEATURE_LABELS = {
  canUseQuotations: 'Quotations',
  canUseInvoices: 'Invoices',
  canUseAppointments: 'Appointments',
  canUsePDF: 'PDF / Email / WhatsApp',
  canUseCSVImport: 'CSV Import',
  canUseContentCalendar: 'Tasks',
  canUseAdvancedReports: 'Advanced Reports',
  canUseWebhooks: 'Webhooks & Lead Integrations',
};

export const CYCLES = [
  { key: 'monthly',    label: 'Monthly',     field: 'price',           per: '/mo'  },
  { key: 'quarterly',  label: 'Quarterly',   field: 'quarterlyPrice',  per: '/qtr' },
  { key: 'halfYearly', label: 'Half-Yearly', field: 'halfYearlyPrice', per: '/6mo' },
  { key: 'yearly',     label: 'Yearly',      field: 'yearlyPrice',     per: '/yr'  },
];

export const getAvailableCycles = (plans) => CYCLES.filter((c) =>
  c.key === 'monthly' || plans.some((p) => Number(p[c.field]) > 0)
);

export const getPrice = (plan, cycle) =>
  cycle.key === 'monthly' ? plan.price : plan[cycle.field];

export const getSaving = (plan, cycle) => {
  if (cycle.key === 'monthly') return null;
  const months = cycle.key === 'quarterly' ? 3 : cycle.key === 'halfYearly' ? 6 : 12;
  const monthlyTotal = Number(plan.price) * months;
  const cyclePrice = Number(plan[cycle.field]);
  if (!cyclePrice || !monthlyTotal) return null;
  const saved = monthlyTotal - cyclePrice;
  if (saved <= 0) return null;
  return { saved, pct: Math.round((saved / monthlyTotal) * 100) };
};

// Shared plan-card grid used by both the dismissable pre-expiry PlansModal and
// the non-dismissable post-expiry RenewalRequired full page, so the pricing
// card markup isn't duplicated between the two.
const PlanGrid = ({ plans, billing, onSelectPlan, selectingPlanId, autoRenew = true }) => {
  const activeCycle = CYCLES.find((c) => c.key === billing) || CYCLES[0];
  // Payments paused by the server: plans stay visible, buying is off, WhatsApp is the way to renew.
  const { paused, message } = usePaymentsPaused();

  return (
    <>
    {paused && <div style={s.pausedNote}>⏸ {message}</div>}
    <div style={s.grid}>
      {plans.map((plan, i) => {
        const isPopular = i === Math.floor(plans.length / 2);
        const price = getPrice(plan, activeCycle);
        const saving = getSaving(plan, activeCycle);
        const selecting = selectingPlanId === plan.id;
        return (
          <div key={plan.id} style={{ ...s.card, ...(isPopular ? s.cardPopular : {}) }}>
            {isPopular && <div style={s.popularBadge}>Most Popular</div>}
            <div style={s.planName}>{plan.displayName || plan.name}</div>
            <div style={s.planPrice}>
              {plan.price === 0 || plan.price === '0.00'
                ? <span style={s.priceNum}>Free</span>
                : Number(price) > 0
                  ? <><span style={s.priceNum}>₹{Number(price).toLocaleString('en-IN')}</span><span style={s.pricePer}>{activeCycle.per}</span></>
                  : <><span style={s.priceNum}>₹{Number(plan.price).toLocaleString('en-IN')}</span><span style={s.pricePer}>/mo</span></>
              }
            </div>
            {plan.price !== 0 && plan.price !== '0.00' && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>+ 18% GST</div>
            )}
            {saving && (
              <div style={{ fontSize: 12, color: 'var(--success)', marginBottom: 4 }}>
                Save ₹{saving.saved.toLocaleString('en-IN')} ({saving.pct}% off vs monthly)
              </div>
            )}
            {plan.description && <p style={s.planDesc}>{plan.description}</p>}

            <div style={s.limits}>
              <div style={s.limitRow}><span style={s.limitIcon}>👥</span>{plan.maxUsersPerWorkspace} users per workspace</div>
              <div style={s.limitRow}><span style={s.limitIcon}>🎯</span>{plan.maxLeadsTotal} leads total</div>
              <div style={s.limitRow}><span style={s.limitIcon}>📁</span>{plan.maxWorkspaces} workspace{plan.maxWorkspaces > 1 ? 's' : ''}</div>
              <div style={s.limitRow}><span style={s.limitIcon}>📅</span>{plan.durationDays} days validity</div>
            </div>

            <div style={s.features}>
              {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                <div key={key} style={s.featureRow}>
                  <span style={{ ...s.featureDot, background: plan[key] ? 'var(--success)' : 'var(--border)' }}>
                    {plan[key] ? '✓' : '✗'}
                  </span>
                  <span style={{ color: plan[key] ? 'var(--text)' : 'var(--text-muted)' }}>{label}</span>
                </div>
              ))}
            </div>

            <button
              style={{ ...s.selectBtn, ...(isPopular && !paused ? s.selectBtnPopular : {}), ...(paused ? s.selectBtnPaused : {}) }}
              onClick={() => onSelectPlan(plan)}
              disabled={selecting || paused}
            >
              {paused ? 'Online payment paused' : selecting ? 'Starting checkout…' : autoRenew ? '💳 Subscribe with auto-renew' : '💳 Pay once for this Plan'}
            </button>
            <a href={buildWhatsAppLink(plan)} target="_blank" rel="noopener noreferrer" style={paused ? s.whatsappPrimary : s.whatsappLink}>
              {paused ? 'Renew via WhatsApp' : 'or renew via WhatsApp instead'}
            </a>
          </div>
        );
      })}
    </div>
    </>
  );
};

const s = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 },
  card: { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 14, padding: '22px 18px', position: 'relative', display: 'flex', flexDirection: 'column' },
  cardPopular: { border: '1.5px solid var(--accent2)', boxShadow: '0 0 28px rgba(155,107,255,0.18)' },
  popularBadge: { position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, var(--accent), var(--accent2))', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap' },
  planName: { fontSize: 18, fontWeight: 700, textTransform: 'capitalize', marginBottom: 8, color: 'var(--text)' },
  planPrice: { marginBottom: 10 },
  priceNum: { fontSize: 26, fontWeight: 800, color: 'var(--accent)' },
  pricePer: { fontSize: 13, color: 'var(--text-muted)', marginLeft: 3 },
  planDesc: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 },
  limits: { marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 7 },
  limitRow: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text)' },
  limitIcon: { fontSize: 13 },
  features: { display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 20, paddingTop: 14, borderTop: '1px solid var(--border)' },
  featureRow: { display: 'flex', alignItems: 'center', gap: 9, fontSize: 12 },
  featureDot: { width: 16, height: 16, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', flexShrink: 0 },
  selectBtn: { marginTop: 'auto', width: '100%', padding: '11px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  selectBtnPopular: { background: 'linear-gradient(135deg, var(--accent), var(--accent2))', border: 'none', color: '#fff' },
  whatsappLink: { display: 'block', textAlign: 'center', fontSize: 11.5, color: 'var(--text-muted)', textDecoration: 'underline', marginTop: 8 },
  selectBtnPaused: { opacity: 0.5, cursor: 'not-allowed' },
  whatsappPrimary: { display: 'block', textAlign: 'center', fontSize: 13, fontWeight: 600, padding: '10px', borderRadius: 9, background: '#25D366', color: '#fff', textDecoration: 'none', marginTop: 8 },
  pausedNote: { marginBottom: 16, padding: '12px 14px', borderRadius: 10, border: '1px solid var(--warning, #f59e0b)', background: 'rgba(245,158,11,0.12)', color: 'var(--text)', fontSize: 13, lineHeight: 1.5 },
};

export default PlanGrid;
